/**
 * Table column sorting.
 *
 * Sort state is a nullable column key plus a direction. Null means "the view's
 * default order", which is a real third state rather than an absence: the roster
 * default is the manual drag-and-drop order, and a user who sorts by a column
 * has no other way back to it.
 *
 * When `column` is null, `direction` is unspecified — don't read it, and don't
 * "fix" the guards that skip it by folding null into the type; `TableSort` is
 * consumed as an optional prop, and `TableSort | null` would give two spellings
 * of "no sort" at every call site.
 */

export type SortDirection = "asc" | "desc";

/**
 * The non-tag sortable columns, mirroring the API's allowlist in
 * `ProspectSortSpec`. Kept in sync by hand across the two languages; the API
 * ignores anything it doesn't recognize and falls back to its default order.
 */
export const PROSPECT_SORT_COLUMNS = [
  "name",
  "position",
  "gradYear",
  "jerseyNumber",
] as const;

export type ProspectStaticSortColumn = (typeof PROSPECT_SORT_COLUMNS)[number];

/** A tag-category column, encoded as the API's `tag_<NameWithoutSpaces>` key. */
export type TagSortColumn = `tag_${string}`;

export type ProspectSortColumn = ProspectStaticSortColumn | TagSortColumn;

export type TableSort = {
  /** Column key sent to the API as `sortBy`. Null for the default order. */
  readonly column: ProspectSortColumn | null;
  readonly direction: SortDirection;
};

// Frozen because it is handed out by reference as a default prop, as initial
// state, and as nextTableSort's third-click result — one accidental mutation
// would poison all three.
export const DEFAULT_TABLE_SORT: TableSort = Object.freeze({
  column: null,
  direction: "asc" as SortDirection,
});

const TAG_COLUMN_PREFIX = "tag_";

const isStaticSortColumn = (value: string): value is ProspectStaticSortColumn =>
  (PROSPECT_SORT_COLUMNS as readonly string[]).includes(value);

const isTagSortColumn = (value: string): value is TagSortColumn =>
  value.startsWith(TAG_COLUMN_PREFIX) && value.length > TAG_COLUMN_PREFIX.length;

/**
 * Parse an untrusted `sortBy` (a URL param) into a column the API accepts.
 *
 * Without this, any string in the URL becomes an "active" sort: the table draws
 * a sort arrow and hides drag-to-reorder while the server, which ignores keys it
 * doesn't recognize, returns rows in its default order. The UI would be claiming
 * a sort that isn't applied.
 *
 * Tag categories are per-organization runtime config, so `tag_Anything` can only
 * be shape-checked here — the server stays the authority on whether the category
 * exists. Rejecting malformed non-tag keys is the part worth doing client-side.
 */
export const parseSortColumn = (
  value: string | null
): ProspectSortColumn | null => {
  if (!value) return null;
  return isStaticSortColumn(value) || isTagSortColumn(value) ? value : null;
};

/**
 * Read a sort out of URL params, falling back to the default order when the
 * column is absent or unusable.
 */
export const parseTableSort = (
  column: string | null,
  direction: string | null
): TableSort => {
  const parsed = parseSortColumn(column);
  if (!parsed) return DEFAULT_TABLE_SORT;
  return { column: parsed, direction: direction === "desc" ? "desc" : "asc" };
};

/**
 * Advance the sort state for a click on `column`, cycling
 * ascending → descending → default.
 *
 * Clicking a different column starts that column at ascending rather than
 * inheriting the previous column's direction, which would otherwise make the
 * first click on a new header land on a direction the user did not ask for.
 */
export const nextTableSort = (
  current: TableSort,
  column: ProspectSortColumn
): TableSort => {
  if (current.column !== column) {
    return { column, direction: "asc" };
  }

  if (current.direction === "asc") {
    return { column, direction: "desc" };
  }

  return DEFAULT_TABLE_SORT;
};

/** The `aria-sort` value for a header cell, for screen-reader announcement. */
export const ariaSortFor = <C extends string = ProspectSortColumn>(
  sort: { readonly column: C | null; readonly direction: SortDirection },
  column: C
): "ascending" | "descending" | "none" => {
  if (sort.column !== column) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
};

/** Serializes a tag category name to the API's `tag_<Name>` sort key. */
export const tagSortColumn = (tagName: string): TagSortColumn =>
  `tag_${tagName.replace(/\s+/g, "")}`;
